import React, { useMemo } from "react";
import { CalendarDateRangeIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

export default function CardRdv({
  date,
  date_creation, // date Airtable "date_création"
  heure_debut,
  heure_fin,
  type_rdv,
  statut,
  id,
}) {
  // --------- helpers
  const toInt = (v) => {
    const n = parseInt(String(v ?? "").replace(/[^\d]/g, ""), 10);
    return Number.isFinite(n) ? n : null;
  };

  const parseDate = (dateStr, fallbackYear) => {
    if (!dateStr) return null;

    // YYYY-MM-DD
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/;
    const mIso = String(dateStr).match(iso);
    if (mIso) {
      return {
        y: Number(mIso[1]),
        m: Number(mIso[2]) - 1,
        d: Number(mIso[3]),
      };
    }

    // DD/MM ou DD/MM/YYYY
    const fr = /^(\d{2})\/(\d{2})(?:\/(\d{4}))?$/;
    const mFr = String(dateStr).match(fr);
    if (mFr) {
      return {
        y: mFr[3] ? Number(mFr[3]) : fallbackYear,
        m: Number(mFr[2]) - 1,
        d: Number(mFr[1]),
      };
    }

    return null;
  };

  const rdvDate = useMemo(() => {
    const fallbackYear = date_creation
      ? new Date(date_creation).getFullYear()
      : null;

    const d = parseDate(date, fallbackYear);
    if (!d?.y) return null;

    const time = String(heure_fin ?? heure_debut ?? "23:59");
    const [h, m] = time.includes(":")
      ? time.split(":").map(toInt)
      : [toInt(time), 0];

    return new Date(d.y, d.m, d.d, h ?? 23, m ?? 59, 0);
  }, [date, date_creation, heure_debut, heure_fin]);

  const isPast = useMemo(
    () => (rdvDate ? rdvDate.getTime() < Date.now() : false),
    [rdvDate]
  );

  // ✅ si le RDV est validé -> on cache le bouton "Valider"
  const isValidated = String(statut || "").toLowerCase() === "valide";

  // --------- UI
  return (
    <div className="mt-3 border-b border-gray-200 bg-white rounded-xl p-4 flex flex-row gap-2 items-center justify-between">
      {/* Date */}
      <div className="flex flex-row items-center gap-2">
        <CalendarDateRangeIcon className="size-12 text-gray-400" />
        <div className="font-bold text-gray-900 ml-2">
          {date}
          <br />
          {heure_debut}h à {heure_fin}h
        </div>
      </div>

      {/* Type */}
      <div>
        {type_rdv === "presentiel" && <>À l'étude</>}
        {type_rdv === "telephone" && <>Par téléphone</>}
        {type_rdv === "visio" && <>En visio</>}
      </div>

      {/* Statut */}
      <div>
        {isPast ? (
          <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-bold text-gray-600">
            Rendez-vous passé
          </span>
        ) : (
          <>
            {isValidated && (
              <span className="inline-flex items-center rounded-md bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-800">
                Validée
              </span>
            )}
            {String(statut || "").toLowerCase() === "attente validation" && (
              <span className="inline-flex items-center rounded-md bg-orange-100 px-2 py-1 text-xs font-bold text-orange-800">
                À valider
              </span>
            )}
            {String(statut || "").toLowerCase() === "annule" && (
              <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-1 text-xs font-bold text-red-800">
                Annulé
              </span>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      {!isPast && (
        <div className="flex flex-row gap-2">
          <Link
            to={`/rdv/${id}`}
            target="blank"
            className="btn-secondary rounded-full"
          >
            Modifier
          </Link>

          {/* ✅ bouton Valider affiché seulement si NON validé */}
          {!isValidated && (
            <Link to={`/rdv/${id}`} target="blank" className="btn-main">
              Valider
            </Link>
          )}
        </div>
      )}
    </div>
  );
}